import React, { useEffect, useRef, useState } from "react";
import {
  deleteDoc,
  deleteField,
  doc,
  getDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import { useAuth } from "../../providers/AuthProvider";
import { hasAnyGrant } from "../../services/shared";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useParams } from "react-router-dom";
import { ConfigLP } from "../../types";

interface LpAgentDoc {
  title: string;
  org: string;
  hash: string;
  createdAt?: Timestamp;
  config: string;
  shortLink?: string;
  userRef?: string;
  user_photo?: string;
}

const TIPOLOGIA_OPTIONS = ["Web", "Telefono", "Referral", "Agenzia"];
const CANALE_OPTIONS = ["Organico", "Paid", "Referral", "Direct"];

export default function LpAgentDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { grants } = useAuth();
  const canDelete = hasAnyGrant(grants, ["admin", "lp_admin"]);

  const [lp, setLp] = useState<LpAgentDoc | null>(null);
  const [config, setConfig] = useState<ConfigLP | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Edit mode
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editCampagnaID, setEditCampagnaID] = useState("");
  const [editFornitore, setEditFornitore] = useState("");
  const [editTipologiaLead, setEditTipologiaLead] = useState("");
  const [editCanale, setEditCanale] = useState("");
  const [editSegnalatoreID, setEditSegnalatoreID] = useState("");
  const [editPhotoMode, setEditPhotoMode] = useState<"url" | "file">("url");
  const [editPhotoUrl, setEditPhotoUrl] = useState("");
  const [editPhotoFile, setEditPhotoFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!id) return;
    getDoc(doc(db, "lp", id))
      .then((snap) => {
        if (!snap.exists()) {
          setError("Landing page non trovata");
          return;
        }
        const data = snap.data() as LpAgentDoc;
        setLp(data);
        try {
          setConfig(JSON.parse(data.config));
        } catch {
          console.error("Config non valida");
        }
      })
      .catch((err) => {
        console.error(err);
        setError("Errore nel caricamento della landing page");
      })
      .finally(() => setLoading(false));
  }, [id]);

  const enterEditMode = () => {
    if (!lp || !config) return;
    setEditTitle(lp.title);
    setEditCampagnaID(String(config.campagnaID ?? ""));
    setEditFornitore(config.c__fowc2kq5 ?? "");
    setEditTipologiaLead(config.c__BYBIDFRZ ?? "");
    setEditCanale(config.c__hyez2gj1 ?? "");
    setEditSegnalatoreID(config.c__3j8d94nv != null ? String(config.c__3j8d94nv) : "");
    setEditPhotoMode("url");
    setEditPhotoUrl(lp.user_photo ?? "");
    setEditPhotoFile(null);
    setError(null);
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!id) return;
    if (!editTitle.trim()) {
      setError("Il titolo è obbligatorio");
      return;
    }
    if (!editCampagnaID || isNaN(Number(editCampagnaID))) {
      setError("ID Campagna deve essere un numero");
      return;
    }
    if (!editFornitore.trim()) {
      setError("Il fornitore è obbligatorio");
      return;
    }
    if (!editTipologiaLead) {
      setError("Seleziona la tipologia lead");
      return;
    }
    if (!editCanale) {
      setError("Seleziona il canale");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      let resolvedPhotoUrl: string | undefined = lp?.user_photo;
      if (editPhotoMode === "file" && editPhotoFile) {
        const path = lp?.userRef
          ? `agent_photos/${lp.userRef}/${Date.now()}_${editPhotoFile.name}`
          : `agent_photos/_unassigned/${id}/${Date.now()}_${editPhotoFile.name}`;
        const fileRef = storageRef(storage, path);
        await uploadBytes(fileRef, editPhotoFile);
        resolvedPhotoUrl = await getDownloadURL(fileRef);
      } else if (editPhotoMode === "url") {
        resolvedPhotoUrl = editPhotoUrl.trim() || undefined;
      }

      const newConfig: ConfigLP = {
        campagnaID: Number(editCampagnaID),
        c__fowc2kq5: editFornitore,
        c__BYBIDFRZ: editTipologiaLead,
        c__hyez2gj1: editCanale,
        ...(editSegnalatoreID && { c__3j8d94nv: Number(editSegnalatoreID) }),
      };
      await updateDoc(doc(db, "lp", id), {
        title: editTitle.trim(),
        config: JSON.stringify(newConfig),
        user_photo: resolvedPhotoUrl ?? deleteField(),
      });
      setLp((prev) =>
        prev
          ? { ...prev, title: editTitle.trim(), user_photo: resolvedPhotoUrl }
          : prev
      );
      setConfig(newConfig);
      setEditMode(false);
    } catch (err) {
      console.error(err);
      setError("Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const copyPublicLink = () => {
    if (!id) return;
    navigator.clipboard.writeText(`${window.location.origin}/lp-agente/${id}`);
  };

  const handleDelete = async () => {
    if (!id || !window.confirm("Eliminare questa landing page?")) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, "lp", id));
      navigate("/lp-agents");
    } catch (err) {
      console.error(err);
      setError("Errore durante l'eliminazione");
      setDeleting(false);
    }
  };

  if (loading)
    return (
      <div className="container">
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </div>
    );

  if (error && !lp)
    return (
      <div className="container">
        <Alert severity="error">{error}</Alert>
      </div>
    );

  if (!lp) return null;

  return (
    <div className="container">
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/lp-agents")}
        >
          Lista LP
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* ── Intestazione ── */}
      {editMode ? (
        <TextField
          label="Titolo"
          value={editTitle}
          onChange={(e) => setEditTitle(e.target.value)}
          fullWidth
          sx={{ mb: 2 }}
          required
        />
      ) : (
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          {lp.title}
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Org: {lp.org}
        {lp.createdAt &&
          ` · Creata il ${lp.createdAt.toDate().toLocaleString("it-IT")}`}
      </Typography>

      {/* ── Azioni rapide ── */}
      {!editMode && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mb: 3,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={copyPublicLink}
            size="small"
          >
            Copia link pubblico
          </Button>
          {lp.shortLink && (
            <Chip
              label={`https://preq.affida.credit/s2/${lp.shortLink}`}
              size="small"
              onClick={() =>
                navigator.clipboard.writeText(
                  `https://preq.affida.credit/s2/${lp.shortLink}`
                )
              }
              clickable
            />
          )}
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={enterEditMode}
            size="small"
          >
            Modifica
          </Button>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* ── Configurazione ── */}
      <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
        Configurazione
      </Typography>

      {editMode ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            label="ID Campagna"
            type="number"
            value={editCampagnaID}
            onChange={(e) => setEditCampagnaID(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Fornitore"
            value={editFornitore}
            onChange={(e) => setEditFornitore(e.target.value)}
            required
            fullWidth
          />
          <TextField
            select
            label="Tipologia Lead"
            value={editTipologiaLead}
            onChange={(e) => setEditTipologiaLead(e.target.value)}
            required
            fullWidth
          >
            {TIPOLOGIA_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Canale"
            value={editCanale}
            onChange={(e) => setEditCanale(e.target.value)}
            required
            fullWidth
          >
            {CANALE_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Segnalatore ID"
            type="number"
            value={editSegnalatoreID}
            onChange={(e) => setEditSegnalatoreID(e.target.value)}
            fullWidth
          />

          <Divider />
          <Typography variant="body2" fontWeight={500}>
            Foto agente
          </Typography>
          <ToggleButtonGroup
            value={editPhotoMode}
            exclusive
            onChange={(_, v) => v && setEditPhotoMode(v)}
            size="small"
          >
            <ToggleButton value="url">Inserisci URL</ToggleButton>
            <ToggleButton value="file">Carica file</ToggleButton>
          </ToggleButtonGroup>

          {editPhotoMode === "url" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <TextField
                label="URL foto agente"
                value={editPhotoUrl}
                onChange={(e) => setEditPhotoUrl(e.target.value)}
                fullWidth
                helperText="Lascia vuoto per rimuovere la foto"
              />
              {editPhotoUrl && (
                <Box
                  component="img"
                  src={editPhotoUrl}
                  alt="Anteprima"
                  sx={{
                    maxHeight: 120,
                    maxWidth: 200,
                    borderRadius: 1,
                    objectFit: "cover",
                    border: "1px solid #e0e0e0",
                  }}
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.display = "none";
                  }}
                />
              )}
            </Box>
          )}

          {editPhotoMode === "file" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={(e) => setEditPhotoFile(e.target.files?.[0] ?? null)}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => fileInputRef.current?.click()}
                sx={{ alignSelf: "flex-start" }}
              >
                {editPhotoFile ? "Cambia immagine" : "Seleziona immagine"}
              </Button>
              {editPhotoFile && (
                <>
                  <Typography variant="caption" color="text.secondary">
                    {editPhotoFile.name}
                  </Typography>
                  <Box
                    component="img"
                    src={URL.createObjectURL(editPhotoFile)}
                    alt="Anteprima"
                    sx={{
                      maxHeight: 120,
                      maxWidth: 200,
                      borderRadius: 1,
                      objectFit: "cover",
                      border: "1px solid #e0e0e0",
                    }}
                  />
                </>
              )}
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              onClick={handleSaveEdit}
              disabled={saving}
            >
              {saving ? <CircularProgress size={20} color="inherit" /> : "Salva modifiche"}
            </Button>
            <Button
              onClick={() => {
                setEditMode(false);
                setError(null);
              }}
              disabled={saving}
            >
              Annulla
            </Button>
          </Box>
        </Box>
      ) : config ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography variant="body2">
            ID Campagna: <strong>{config.campagnaID}</strong>
          </Typography>
          <Typography variant="body2">
            Fornitore: <strong>{config.c__fowc2kq5}</strong>
          </Typography>
          <Typography variant="body2">
            Tipologia Lead: <strong>{config.c__BYBIDFRZ}</strong>
          </Typography>
          <Typography variant="body2">
            Canale: <strong>{config.c__hyez2gj1}</strong>
          </Typography>
          {config.c__3j8d94nv != null && (
            <Typography variant="body2">
              Segnalatore ID: <strong>{config.c__3j8d94nv}</strong>
            </Typography>
          )}
        </Box>
      ) : (
        <Typography variant="body2" color="text.secondary">
          Config non disponibile
        </Typography>
      )}

      {/* ── Foto agente ── */}
      {lp.user_photo && !editMode && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
            Foto agente
          </Typography>
          <Box
            component="img"
            src={lp.user_photo}
            alt="Foto agente"
            sx={{
              maxHeight: 120,
              maxWidth: 200,
              borderRadius: 1,
              objectFit: "cover",
              border: "1px solid #e0e0e0",
            }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </>
      )}

      {/* ── Elimina ── */}
      {canDelete && !editMode && (
        <>
          <Divider sx={{ my: 3 }} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDelete}
            disabled={deleting}
            size="small"
          >
            {deleting ? "Eliminazione…" : "Elimina landing page"}
          </Button>
        </>
      )}
    </div>
  );
}
